import {Inject, Injectable} from '@angular/core';
import {DOCUMENT} from '@angular/common';
import {AppConfigService} from '../../../../../services/app-config.service';
import {SchemaCodeSnippetIdEnum} from '../../../../../enums/SchemaEnums';
import {ToolsService} from '../../../../../services/tools.service';
import {CodeSnippetsService} from '../../../../../services/code-snippets.service';
import {WPPostCareServiceTypePage} from '../typings/WPPostCareServiceTypePage';
import {WPContentSectionWithButtonAndSummary} from '../typings/WPContentSectionWithButtonAndSummary';
import {ContentLabelsEnum} from '../enums/content-labels.enum';
import {WPPostSymptomPage} from '../typings/WPPostSymptomPage';

@Injectable()
export class SearchContentSchemaService {
  constructor(
    private headerScriptService: CodeSnippetsService,
    private tools: ToolsService,
    @Inject(DOCUMENT) private doc: any,
    private appConfigService: AppConfigService,
  ) {}

  public addSchema(symptomPage: WPPostSymptomPage): void {
    const schema = this.createMedicalWebPageJsonSchema(symptomPage);
    this.headerScriptService.addScriptToHead(schema);

    const schema2 = this.createFaqJsonSchema(symptomPage);
    if (schema2) {
      this.headerScriptService.addScriptToHead(schema2);
    }
  }

  public removeSchema(): void {
    this.headerScriptService.removeScriptsFromHead(`#${SchemaCodeSnippetIdEnum.MEDICAL_WEBPAGE}`);
    this.headerScriptService.removeScriptsFromHead(`#${SchemaCodeSnippetIdEnum.FAQ}`);
  }

  private createFaqJsonSchema(page: WPPostCareServiceTypePage | WPPostSymptomPage): any {
    const filteredContentSections: Array<WPContentSectionWithButtonAndSummary> = page.acf.contentSections.filter(
      (item) =>
        item.button.value === ContentLabelsEnum.KOSTNAD ||
        item.button.value === ContentLabelsEnum.FORDELAR ||
        item.button.value === ContentLabelsEnum.NACKDELAR ||
        item.button.value === ContentLabelsEnum.VARDGIVARE,
    );

    if (filteredContentSections && filteredContentSections.length > 0) {
      const entities = [];
      filteredContentSections.forEach((section) => {
        entities.push({
          '@type': 'Question',
          name: section.title,
          acceptedAnswer: {
            '@type': 'Answer',
            text: this.tools.removeHtmlTags(section.summary),
          },
        });
      });

      const jsonSchema = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: entities,
      };

      const schemaScript = this.doc.createElement('script');
      schemaScript.type = 'application/ld+json';
      schemaScript.id = SchemaCodeSnippetIdEnum.FAQ;
      schemaScript.innerHTML = JSON.stringify(jsonSchema);
      return schemaScript;
    }
    return null;
  }

  private createMedicalWebPageJsonSchema(symptomPage: WPPostSymptomPage): any {
    const organization = {
      '@type': 'Organization',
      name: 'Vården Online Sverige AB',
      logo: `${this.appConfigService.appConfig.amazonS3Url}assets/varden-logo-nocross.webp`,
      url: 'https://www.varden.se',
      contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'Content team',
        email: 'info@varden.se',
        telephone: '+46-8-509-106-99',
        areaServed: 'SE',
      },
    };
    const publisher = {
      ...organization,
      sameAs: ['https://www.facebook.com/varden.se', 'https://www.instagram.com/varden.se'],
    };

    let content = '';
    symptomPage.acf.contentSections.forEach((section) => {
      content += section.title + '. ' + section.content + ' ';
    });

    const jsonSchema = {
      '@context': 'https://schema.org',
      '@type': 'MedicalWebPage',
      publisher: publisher,
      headline: symptomPage.title.rendered,
      description: this.tools.removeHtmlTags(content),
      datePublished: symptomPage.date,
      dateModified: symptomPage.modified,
      lastReviewed: symptomPage.modified,
      author: publisher,
      reviewedBy: organization,
    };

    const schemaScript = this.doc.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.id = SchemaCodeSnippetIdEnum.MEDICAL_WEBPAGE;
    schemaScript.innerHTML = JSON.stringify(jsonSchema);
    return schemaScript;
  }
}
